import { isAfter } from 'date-fns';
import { LuPen } from 'react-icons/lu';

import { GlobalAccount, GUID, TaskTimelineEntryView } from '@ndvr/customer-api';

import { Button } from '@/components/ui/button';
import { useGlobalAccount } from '@/views/household/household-queries';
import { useTaskTimeline } from '@/views/tasks/task-queries';
import { parseLocalDate } from 'utils/date-formatter';

import { useDockState } from '../dock-state';
import { DockItemType, UpdateTaskDockItem } from '../dock-state-types';
import { DockItemProps } from './DockItem';

export const DockItemTitle = (props: DockItemProps & { isHeader?: boolean }) => {
    if (props.item.type === DockItemType.CREATE_NOTE) {
        return <HouseholdTitle title="New Note" householdGuid={props.item.data.globalAccountGUID} />;
    }
    if (props.item.type === DockItemType.UPDATE_NOTE) {
        // Read-only view leads with the note itself; the household moved into the body link.
        if (props.item.data.viewMode === 'view') {
            return <span className="truncate font-semibold">{props.item.data.subject || 'Untitled Note'}</span>;
        }
        return <HouseholdTitle title="Update Note" householdGuid={props.item.data.globalAccountGUID} />;
    }
    if (props.item.type === DockItemType.CREATE_TASK) {
        return <HouseholdTitle title="New Task" householdGuid={props.item.data.globalAccountGUID} />;
    }
    if (props.item.type === DockItemType.UPDATE_TASK) {
        return <TaskTitle item={props.item} isHeader={props.isHeader} />;
    }
    if (props.item.type === DockItemType.CREATE_OPPORTUNITY) {
        return <HouseholdTitle title="New Opportunity" householdGuid={props.item.data.prospectGUID} />;
    }
    if (props.item.type === DockItemType.UPDATE_OPPORTUNITY) {
        return <HouseholdTitle title="Update Opportunity" householdGuid={props.item.data.prospectGUID} />;
    }
    return <span>Unknown Dock Item</span>;
};

export const hasTaskUpdate = (timeline: TaskTimelineEntryView[] | undefined, openedAt: string): boolean => {
    if (!timeline?.length) return false;
    const latestActivityAt = timeline
        .map((e) => parseLocalDate(e.timestamp))
        .reduce<Date | undefined>((max, d) => (!max || d > max ? d : max), undefined);
    return !!latestActivityAt && isAfter(latestActivityAt, new Date(openedAt));
};

const TaskTitle = ({ item, isHeader }: { item: UpdateTaskDockItem; isHeader?: boolean }) => {
    const { data: timeline } = useTaskTimeline(item.data.taskGUID);
    const { updateDockItem } = useDockState();

    return (
        <div className="group flex min-w-0 items-center gap-1.5">
            {hasTaskUpdate(timeline, item.openedAt) && <div className="bg-seafoam-200 h-2 w-2 shrink-0 rounded-full" />}
            <span className="truncate font-semibold">{item.data.title || 'Untitled Task'}</span>
            {isHeader && (item.data.viewMode ?? 'view') === 'view' && (
                <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Edit task"
                    className="h-6 w-6 shrink-0"
                    onClick={() => updateDockItem({ ...item, data: { ...item.data, viewMode: 'edit' } })}
                >
                    <LuPen className="h-3.5 w-3.5" />
                </Button>
            )}
        </div>
    );
};

export const HouseholdTitle = (props: { title: string; householdGuid?: GUID<GlobalAccount> }) => {
    const globalAccountQuery = useGlobalAccount(props.householdGuid);
    if (!globalAccountQuery.data) return <span>{props.title}</span>;
    return (
        <div className="flex flex-row items-center gap-2 truncate">
            <span>{props.title}</span>
            <span>-</span>
            <span className="font-semibold">{globalAccountQuery.data.globalAccountNamePlain}</span>
        </div>
    );
};
